import React, { useState } from "react";
import { createWebSocketClient } from "../utils/websocketUtils";
import { useTaxDispatch, useTaxSelector } from "../redux/general/reduxStore";
import { getActuatorInfo } from "../backend/taxguru/client/actuatorClient";
import { setActuatorInfo } from "../redux/applicationSlice";
import { initialiseIfAccountant, initialiseIfClient, initializeUserInfo } from "../modules/account/function/accountUtils";
import { initialiseTableStatesThunk } from "../redux/general/reduxTableStateUpdates";
import { SimpleUserInfoResponse } from "@mm-products/java-multima-framework";
import { initialiseAppSettingsThunk } from "../redux/applicationSettingsSlice.persistor";

let didInit = false;

async function initUserAdditionalInfo(data: SimpleUserInfoResponse) {
    if (!data?.id) {
        return;
    }
    try {
        await initialiseIfAccountant(data.id);
    } catch (err) {
        /* ignored */
    }
    try {
        await initialiseIfClient(data.id);
    } catch (err) {
        /* ignored */
    }
}

const useInitialiseApplication = () => {
    const username = useTaxSelector((state) => state.user.username);
    const [initialised, setInitialised] = useState(username !== undefined);
    const dispatch = useTaxDispatch();

    const reinit = React.useCallback(() => {
        const initActuatorInfo = () => getActuatorInfo().then(({ data }) => dispatch(setActuatorInfo(data)));
        return initializeUserInfo()
            .then(async (data) => {
                if (data?.id) {
                    await initUserAdditionalInfo(data);
                    dispatch(initialiseTableStatesThunk(data.id));
                    dispatch(initialiseAppSettingsThunk(data.id));
                }
                await initActuatorInfo();
            })
            .finally(() => {
                setInitialised(true);
            });
    }, [dispatch]);

    React.useEffect(() => {
        if (didInit) {
            return;
        }
        console.debug("Initialising ...");
        reinit();
        didInit = true;
    }, [reinit]);

    React.useEffect(() => {
        if (!username) {
            return;
        }
        const client = createWebSocketClient();
        client.activate();
        return () => {
            client.deactivate();
        };
    }, [username]);

    return {
        initialised,
        username,
        forceInitialisation: reinit,
    };
};

export default useInitialiseApplication;
